import React, { useEffect, useState, useRef, useMemo } from "react";
import {
    BLOG_TERM_TOPICS,
    BLOG_TERM_TRENDS,
    BLOG_TERM_INDUSTRIES,
    BLOG_TERM_AUTHOR,
    BLOG_RESET_FILTERS_TEXT,
    BLOG_RESET_ALL_FILTERS,
    BLOG_PAGE_SIZE,
    BLOG_NO_RESULTS_TEXT,
    blogCategoryFieldMapping,
    BLOG_VALIDATION_NO_RESULTS,
    BLOG_KEYWORD_PLACEHOLDER,
} from "@qlik/qlik/src/lib/constants/blog-constants";
import { fetchBlogListing } from "@qlik/qlik/src/lib/services/blog/api";
import { blogMapper } from "@qlik/qlik/src/lib/util/mappers/contentful-response-mappers";
import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import Pagination from "@qlik/qlik/src/components/Common/Pagination";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import {
    getValidatedCategoryFilters,
    scrollToElement,
    getMicrocopyValueFromKeys,
} from "@qlik/qlik/src/lib/util/helpers";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import useUrlQueryParamsState from "@qlik/qlik/src/hooks/useUrlQueryParamsState";
import BlogCategoryData from "@qlik/qlik/src/graphql/queries/blogs";
import DynamicCheckboxFilterGroup from "@qlik/qlik/src/components/Common/DynamicCheckboxFilterGroup";
import Preloader from "@qlik/qlik/src/components/Common/Preloader";
import MultiCtaBlogCard from "./MultiCtaBlogCard";
import SingleCtaBlogCard from "./SingleCtaBlogCard";
import useSiteMetadata from "@qlik/qlik/src/hooks/useSiteMetaData";

/**
 * Listing component for Blogs with filters & pagination
 * @returns
 */
const BlogListing = ({ data, className }) => {
    const { additionalConfiguration, backgroundColor, selectedFilters = {}, customId } = data || {};
    const customClassName = className ?? ""; // pass custom class
    const preFilters = useMemo(
        () => ({
            author: selectedFilters?.author,
            topic: selectedFilters?.topic,
            trend: selectedFilters?.trend,
            industry: selectedFilters?.industry,
        }),
        [selectedFilters]
    );

    const [listing, setListing] = useState([]); // filtered blogs
    const [total, setTotal] = useState(0); // total items availabel for the current query
    const [categories, setCategories] = useState([]); // set of filter categories - static
    const [currentCategory, setCurrentCategory] = useState(""); // keeps track of the last filtered category
    const [isLoading, setIsLoading] = useState(true);

    // The contentful env and preview status used for this build, to be passed to the Netlify function so that we query resources for the current environment.
    const { env, preview } = useSiteMetadata();

    const [
        resetFilterText,
        resetAllFilterText,
        noResultsText,
        noResultsValidationText,
        searchText,
    ] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        BLOG_RESET_FILTERS_TEXT,
        BLOG_RESET_ALL_FILTERS,
        BLOG_NO_RESULTS_TEXT,
        BLOG_VALIDATION_NO_RESULTS,
        BLOG_KEYWORD_PLACEHOLDER,
    ]);

    const preFiltered = Object.keys(selectedFilters).length > 0;

    const initialFilters = {
        page: 0,
        limit: BLOG_PAGE_SIZE,
        query: "",
        [BLOG_TERM_TOPICS]: preFiltered && preFilters.topic ? [preFilters.topic] : [],
        [BLOG_TERM_TRENDS]: preFiltered && preFilters.trend ? [preFilters.trend] : [],
        [BLOG_TERM_INDUSTRIES]: preFiltered && preFilters.industry ? [preFilters.industry] : [],
        [BLOG_TERM_AUTHOR]: preFiltered && preFilters.author ? [preFilters.author] : [],
    };

    const [filters, setFilters] = useUrlQueryParamsState(initialFilters, blogCategoryFieldMapping);

    const postListWrapperClassName = preFiltered ? "md:col-12 xl:col-12" : "md:col-8 xl:col-9 xl:pl-11.25";

    const {
        allCategoryData,
        authors: { nodes: authorNodes },
    } = BlogCategoryData();

    useEffect(() => {
        if (preFiltered) {
            setFilters((prev) => ({
                ...prev,
                [BLOG_TERM_AUTHOR]: preFilters.author ? [preFilters.author] : [],
                [BLOG_TERM_TOPICS]: preFilters.topic ? [preFilters.topic] : [],
                [BLOG_TERM_TRENDS]: preFilters.trend ? [preFilters.trend] : [],
                [BLOG_TERM_INDUSTRIES]: preFilters.industry ? [preFilters.industry] : [],
            }));
        }
    }, [preFiltered, preFilters, setFilters]);

    // fetch resource entries as per the filter
    const fetchBlogs = useDebouncedCallback(async () => {
        try {
            const results = await fetchBlogListing(filters, env, preview);
            if (!results) throw new Error(noResultsValidationText);
            setTotal(results.results?.total);
            setListing(blogMapper(results.results?.items));
            setIsLoading(false);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    });

    // set category filters with validations
    useEffect(() => {
        // construct category objects
        const resourceCategoryData = Object.entries(allCategoryData).map(([categoryName, categoryFilters]) => {
            const transformedCategoryName = categoryName.replace("_", " ");
            const categoryObj = {
                id: transformedCategoryName,
                title: transformedCategoryName,
                subTerms: [],
            };

            // map author category
            if (transformedCategoryName === BLOG_TERM_AUTHOR) {
                categoryObj.subTerms = categoryFilters.map(({ fieldValue, edges }) => {
                    const foundAuthor = authorNodes?.find((authorObj) => authorObj.id === fieldValue);
                    let title = "";

                    if (foundAuthor) {
                        title = `${foundAuthor.firstName} ${foundAuthor.lastName ? foundAuthor.lastName : ""}`;
                    }

                    const subTermObj = {
                        id: fieldValue,
                        title,
                        isDisabled: false,
                        items: edges.map((item) => item.node.id), // keep the resource items for each category to update filters availability
                    };

                    return subTermObj;
                });
            } else {
                // map other categories
                // construct category filter items for each category
                categoryObj.subTerms = categoryFilters.map(({ fieldValue, edges }) => ({
                    id: fieldValue,
                    title: fieldValue,
                    isDisabled: false,
                    items: edges.map((item) => item.node.id), // keep the resource items for each category to update filters availability
                }));
            }

            // sort category filters in asc
            categoryObj.subTerms.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));

            return categoryObj;
        });

        const validatedCategories = getValidatedCategoryFilters(
            { title: currentCategory, value: filters[currentCategory] },
            resourceCategoryData
        );
        setCategories(validatedCategories);
    }, [filters, currentCategory, allCategoryData, authorNodes]);

    // fetch blog listing as per the filters applied
    useEffect(() => {
        const updateListing = async () => {
            setIsLoading(true);
            await fetchBlogs();
        };

        updateListing();
    }, [filters, fetchBlogs]);

    /**
     * Handle search query updates
     */
    const handleQueryChange = (event) => {
        event.preventDefault();
        const query = event?.target?.value;
        // contentful will only return results for queries more than 2 characters long
        setFilters((prev) => ({ ...prev, page: 0, query }));
    };

    const sectionRef = useRef(null);
    const handlePageChange = (selectedPage) => {
        setFilters((prev) => ({ ...prev, page: selectedPage }));
        scrollToElement(sectionRef);
    };

    const {
        sectionColorMapping: { bgColor },
    } = getColorClassName(backgroundColor);
    const spacingClassName = getSpacingClassName(additionalConfiguration, "py-15");

    // filters which are reflected in filter chips
    // excluding page & limit
    const getFilterChipFilters = () => {
        const filtersObj = { ...filters };
        delete filtersObj.page;
        delete filtersObj.limit;
        return filtersObj;
    };

    // update filter state when filter chip close
    const handleFilterChipClose = (filterCategory, filterValue) => {
        const currentFilterValue = filters[filterCategory];
        let updatedFilter = "";
        if (typeof currentFilterValue !== "string") {
            updatedFilter = currentFilterValue?.filter((filter) => filter !== filterValue);
        }
        setCurrentCategory(filterCategory);
        setFilters((prev) => ({ ...prev, [filterCategory]: updatedFilter }));
    };

    const resetFilters = () => {
        setCurrentCategory("");
        setFilters({
            page: 0,
            limit: BLOG_PAGE_SIZE,
            query: "",
            ...Object.fromEntries(Object.keys(blogCategoryFieldMapping).map((key) => [[key], []])),
        });
    };

    /**
     * Render Blog card based on CTAs available
     * @param {Object} blog - Blog data
     * @returns
     */
    const renderBlogCard = (blog, i) =>
        blog.cta?.length && blog.cta?.length > 1 ? (
            <MultiCtaBlogCard key={i} blog={blog} />
        ) : (
            <SingleCtaBlogCard key={i} blog={blog} />
        );

    return (
        <section
            ref={sectionRef}
            className={`${bgColor} ${spacingClassName} ${customClassName} scroll-m-20`}
            id={customId}
        >
            <div className="container">
                <div className="row">
                    {!preFiltered && (
                        <div className="md:col-4 xl:col-3">
                            <div className="pb-7.5 md:pb-0">
                                <input
                                    className="body w-full h-15 py-4 pl-15 pr-5 mb-7.75 border border-stone bg-filterSearch bg-no-repeat bg-filter-search-icon rounded placeholder:text-gray"
                                    type="text"
                                    placeholder={searchText}
                                    value={filters.query}
                                    onChange={handleQueryChange}
                                />
                                {/* Render filters for each top level category */}
                                {categories?.map(
                                    (category, i) =>
                                        category.subTerms && (
                                            <DynamicCheckboxFilterGroup
                                                key={category.id}
                                                category={category}
                                                filters={filters}
                                                setFilters={setFilters}
                                                setCurrentCategory={setCurrentCategory}
                                                index={i}
                                            />
                                        )
                                )}
                                {Object.values(getFilterChipFilters())
                                    .flat()
                                    .filter(Boolean).length > 0 && (
                                    <button
                                        className="hidden md:inline-block mt-8.75 text-xs lg:text-sm font-medium text-green tracking-wide4 cursor-pointer uppercase"
                                        onClick={resetFilters}
                                    >
                                        <span className="inline-block w-3 h-3 mr-2.5 bg-closeIconGreenMd bg-no-repeat" />
                                        {resetAllFilterText}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <div className={`${postListWrapperClassName}`}>
                        {!preFiltered &&
                            Object.values(getFilterChipFilters())
                                .flat()
                                .filter(Boolean).length > 0 && (
                                <div className="flex flex-wrap gap-2.5 pb-7.5 lg:pb-15 pt-5 sm:pt-0">
                                    <FilterChips
                                        filters={getFilterChipFilters()}
                                        onClose={handleFilterChipClose}
                                        onResetFilters={resetFilters}
                                        resetFiltersLabel={resetFilterText}
                                    />
                                </div>
                            )}
                        {preFiltered && isLoading && (
                            <div className="flex h-full items-center">
                                <Preloader className="mx-auto" />
                            </div>
                        )}
                        {!preFiltered && !isLoading && listing?.length === 0 && (
                            <div className="flex h-full items-center">
                                <NoResults className="w-full text-center text-stone font-medium" text={noResultsText} />
                            </div>
                        )}
                        <div
                            className={
                                // (isLoading ? "blur-sm " : "") +
                                "transition grid gap-6 md:grid-cols-2 xl:grid-cols-3 lg:gap-x-7.5 lg:gap-y-7"
                            }
                        >
                            {listing?.map(renderBlogCard)}
                        </div>
                        <div>
                            <Pagination
                                currentPage={filters.page}
                                limit={filters.limit}
                                total={total}
                                handlePageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BlogListing;
